<template>
  <div
    v-if="isEventNotStarted"
    class="e4s-info-message e4s-vertical-spacer--standard"
    style="padding-top: 8px; padding-bottom: 8px"
  >
    {{ formattedLastCachedDate }}. Reload page for latest.
  </div>

  <div class="e4s-flex-row e4s-gap--standard e4s-justify-flex-row-vert-center">
    <div class="e4s-flex-row e4s-justify-flex-row-vert-center e4s-flex-grow">
      <InputDebounce
        class="e4s-square--right e4s-flex-grow"
        :class="filterSearchTerm.length ? 'e4s-input-field--highlight' : ''"
        placeholder="Search..."
        :value="filterSearchTerm"
        @input="onSearchTermChanged"
      />
      <ButtonGenericV2
        class="e4s-button--auto"
        with-input="right"
        @click="onSearchTermClear"
        :disabled="filterSearchTerm.length === 0"
        text="X"
      />
    </div>
    <div class="e4s-flex-row--end">
      <slot name="after-search" />
    </div>
  </div>

  <div class="e4s-vertical-spacer--standard"></div>

  <div class="e4s-flex-row e4s-gap--standard e4s-flex-row--end e4s-subheader--general">
    <span v-if="hasWaitlist" class="waitlist-count-badge"
      >Waitlist: {{ waitlistEntries.length }} <span v-if="isWaitListClosed">CLOSED</span></span
    >
    <span>Entries: {{ confirmedEntries.length }}</span>
  </div>

  <div class="e4s-vertical-spacer--standard"></div>

  <div class="e4s-flex-row">
    <div class="e4s-flex-column e4s-justify-flex-end e4s-header--500 hybrid-start-race--first-col">
      <div class="e4s-flex-row e4s-justify-flex-row-vert-center">Bib</div>
    </div>
    <div class="e4s-flex-column e4s-justify-flex-end e4s-flex-row--end e4s-header--500">
      Estimated
    </div>
  </div>
  <div class="e4s-vertical-spacer--standard"></div>
  <hr class="dat-e4s-hr-only dat-e4s-hr--light" />

  <!-- Confirmed Entries -->
  <div>
    <HybridStartRace
      class="common-simple-row--top-only"
      v-for="individualEntry in filteredConfirmedEntries"
      :key="individualEntry.entryId"
      :show-position="false"
      :show-bib-lane-track="false"
      :is-track="isTrack"
      :pof10="individualEntry.pof10"
      :urn="individualEntry.URN"
    >
      <template #first-col>
        <div class="e4s-flex-row e4s-gap--small e4s-justify-flex-row-vert-center">
          <span
            class="e4s-lite-number e4s-lite-number--position-lane"
            :style="individualEntry.collected !== 1 ? 'color: var(--slate-400);' : ''"
            >{{ getBibNo(individualEntry) }}</span
          >
        </div>
      </template>
      <template #bibNo>
        <span class="e4s-lite-number">{{ getBibNo(individualEntry) }}</span>
      </template>
      <template #heatNo>
        {{ individualEntry.seeding.heatNo }}
      </template>
      <template #laneNo>
        {{ individualEntry.seeding.laneNo }}
      </template>
      <template #athlete-name>
        <div
          class="e4s-flex-row e4s-gap--small e4s-align-items-center e4s-justify-flex-row-vert-center e4s-lite-table--standard-text"
        >
          {{ individualEntry.firstName + ' ' + individualEntry.surName }}
          {{
            individualEntry.classification > 0 ? ' (' + individualEntry.classification + ')' : ''
          }}
        </div>
      </template>
      <template #affiliation>
        {{ individualEntry.clubName }}
      </template>
      <template #ageGroup>
        {{ '(' + individualEntry.ageGroup + ')' }}
      </template>
      <template #performance>
        <span class="e4s-lite-number e4s-lite-number--score">{{
          getScoreToDisplay(individualEntry.perf, isTrack)
        }}</span>
      </template>

      <template #sb-perf>
        {{ ResultsService.getPo10Value(individualEntry.pof10, 'SB', isTrack) }}
      </template>
      <template #pb-perf>
        {{ ResultsService.getPo10Value(individualEntry.pof10, 'PB', isTrack) }}
      </template>
      <template #po10-link>
        <PowerOfTenLink :urn="individualEntry.URN" image-height="16px" />
      </template>
      <template #extra-row> </template>
      <template #bib-checked-in>
        <div
          class="e4s-flex-row e4s-gap--standard e4s-justify-flex-row-vert-center e4s-subheader--300 e4s-subheader--general"
        >
          <span
            v-if="isCompUsingCheckIn"
            v-text="individualEntry.checkedIn === 1 ? '' : 'Not ' + 'Checked In'"
          ></span>
        </div>
      </template>
    </HybridStartRace>
  </div>

  <!-- Waiting List Section -->

  <!-- hasWaitlist {{ hasWaitlist }} filteredWaitlistEntries.length {{ filteredWaitlistEntries.length }} -->

  <div v-if="filteredWaitlistEntries.length > 0">
    <div class="e4s-vertical-spacer--standard"></div>
    <div class="e4s-waitlist-banner" v-if="hasWaitlist && !isWaitListClosed">
      <span class="e4s-waitlist-banner-icon"></span>
      <div class="e4s-waitlist-banner-text">
        <div class="e4s-waitlist-banner-title">WAITING LIST — NOT CONFIRMED</div>
        <div class="e4s-waitlist-banner-subtitle">
          These athletes are not guaranteed to compete. A place on the waiting list does not mean
          entry to the event.
        </div>
      </div>
    </div>
    <div v-if="hasWaitlist && isWaitListClosed" class="e4s-waitlist-closed-banner">
      <span class="e4s-waitlist-closed-icon"></span>
      <div class="e4s-waitlist-closed-text">
        <div class="e4s-waitlist-closed-title">WAITLIST CLOSED</div>
        <div class="e4s-waitlist-closed-subtitle">
          No further athletes will be added from the waiting list. Please do not contact the
          organisers about waitlist positions.
        </div>
      </div>
    </div>
    <div class="e4s-vertical-spacer--standard"></div>
    <HybridStartRace
      class="common-simple-row--top-only waitlist-row"
      v-for="individualEntry in filteredWaitlistEntries"
      :key="individualEntry.entryId"
      :show-position="false"
      :show-bib-lane-track="false"
      :is-track="isTrack"
      :pof10="individualEntry.pof10"
      :urn="individualEntry.URN"
    >
      <template #first-col>
        <div class="e4s-flex-row e4s-gap--small e4s-justify-flex-row-vert-center">
          <span
            class="e4s-lite-number e4s-lite-number--position-lane"
            :style="individualEntry.collected !== 1 ? 'color: var(--slate-400);' : ''"
            >{{ getBibNo(individualEntry) }}</span
          >
        </div>
      </template>
      <template #bibNo>
        <span class="e4s-lite-number">{{ getBibNo(individualEntry) }}</span>
      </template>
      <template #heatNo>
        {{ individualEntry.seeding.heatNo }}
      </template>
      <template #laneNo>
        {{ individualEntry.seeding.laneNo }}
      </template>
      <template #athlete-name>
        <div
          class="e4s-flex-row e4s-gap--small e4s-align-items-center e4s-justify-flex-row-vert-center e4s-lite-table--standard-text"
        >
          <span class="e4s-clock-icon">
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <circle cx="12" cy="12" r="10"></circle>
              <polyline points="12 6 12 12 16 14"></polyline>
            </svg>
          </span>
          {{ individualEntry.firstName + ' ' + individualEntry.surName }}
          {{
            individualEntry.classification > 0 ? ' (' + individualEntry.classification + ')' : ''
          }}
          <span class="waitlist-badge" v-if="individualEntry.waitlistPosition">
            #{{ individualEntry.waitlistPosition }}
          </span>
        </div>
      </template>
      <template #affiliation>
        {{ individualEntry.clubName }}
      </template>
      <template #ageGroup>
        {{ '(' + individualEntry.ageGroup + ')' }}
      </template>
      <template #performance>
        <span class="e4s-lite-number e4s-lite-number--score">{{
          getScoreToDisplay(individualEntry.perf, isTrack)
        }}</span>
      </template>

      <template #sb-perf>
        {{ ResultsService.getPo10Value(individualEntry.pof10, 'SB', isTrack) }}
      </template>
      <template #pb-perf>
        {{ ResultsService.getPo10Value(individualEntry.pof10, 'PB', isTrack) }}
      </template>
      <template #po10-link>
        <PowerOfTenLink :urn="individualEntry.URN" image-height="16px" />
      </template>
      <template #extra-row> </template>
      <template #bib-checked-in>
        <div
          class="e4s-flex-row e4s-gap--standard e4s-justify-flex-row-vert-center e4s-subheader--300 e4s-subheader--general"
        >
          <span
            v-if="isCompUsingCheckIn"
            v-text="individualEntry.checkedIn === 1 ? '' : 'Not ' + 'Checked In'"
          ></span>
        </div>
      </template>
    </HybridStartRace>
  </div>
</template>

<script setup lang="ts">
import type {
  CompEvent,
  IndividualEntry,
  StoreCompResultsState
} from '@/components/results/store-comp-results'
import { computed, type PropType, ref, watch } from 'vue'
import * as ResultsService from '@/components/results/results-service'
import PowerOfTenLink from '@/common/ui/PowerOfTenLink.vue'
import HybridStartRace from '@/components/results/HybridStartRace.vue'
import InputDebounce from '@/common/ui/fields/input-debounce.vue'
import {
  getBibNo,
  getScoreToDisplay,
  isWaitingListCloseed
} from '@/components/results/results-service'
import ButtonGenericV2 from '@/common/ui/buttons/button-generic-v2.vue'

const props = defineProps({
  storeCompResultsState: {
    type: Object as PropType<StoreCompResultsState>,
    required: true
  },
  scheduledEvent: {
    type: Object as PropType<CompEvent>,
    required: true
  },
  filterSearchTerm: {
    type: String,
    default: ''
  },
  isTrack: {
    type: Boolean,
    required: true
  }
})

const emit = defineEmits<{
  (e: 'searchTermChanged', value: string): void
}>()

watch(
  () => props.scheduledEvent,
  (newVal, oldValue) => {
    console.log('ResultEntries.scheduledEvent changed', newVal, oldValue)
    filterTerm.value = props.filterSearchTerm
  }
)

watch(
  () => props.filterSearchTerm,
  (newVal, oldValue) => {
    if (newVal !== filterTerm.value) {
      console.log('ResultEntries.filterSearchTerm changed', newVal, oldValue)
      filterTerm.value = newVal
    }
  }
)

const filterTerm = ref('')

console.log('ResultEntries.setup() props.scheduledEvent', props.scheduledEvent)

function onSearchTermClear() {
  emit('searchTermChanged', '')
}

function onSearchTermChanged(searchTerm: string) {
  console.log('ResultEntries.onSearchTermChanged', searchTerm)
  emit('searchTermChanged', searchTerm)
}

// computed prop does competion use checkin
const isCompUsingCheckIn = computed(() => {
  return props.storeCompResultsState.competitionOnTheDay.options.checkIn.enabled
})

// computed prop to check if event has not started
const isEventNotStarted = computed(() => {
  const now = new Date()
  const eventDate = new Date(props.scheduledEvent.eventDate)
  return now < eventDate
})

// computed prop to format last cached date
const formattedLastCachedDate = computed(() => {
  const cachedAt = props.storeCompResultsState.competitionOnTheDay.cachedDataSummary.cachedAt
  if (!cachedAt) return ''
  const date = new Date(cachedAt)
  return date.toLocaleString('en-GB', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit'
  })
})

// All entries
const allEntries = computed<IndividualEntry[]>(() => {
  const compEvent = props.scheduledEvent
  return compEvent.entries ? Object.values(compEvent.entries) : []
})

const confirmedEntries = computed<IndividualEntry[]>(() => {
  const entries = allEntries.value.filter((e) => !e.isWaitlisted)
  return ResultsService.sortEntriesByEstimatedPerformance(entries, props.isTrack)
})

const waitlistEntries = computed<IndividualEntry[]>(() => {
  const entries = allEntries.value.filter((e) => e.isWaitlisted)
  return [...entries].sort((a, b) => {
    const nameA = (a.firstName + ' ' + a.surName).toLowerCase()
    const nameB = (b.firstName + ' ' + b.surName).toLowerCase()
    return nameA.localeCompare(nameB)
  })
})

// Search filtering
const matchesSearch = (entry: IndividualEntry): boolean => {
  if (!filterTerm.value) return true
  const term = filterTerm.value.toLowerCase()
  const name = (entry.firstName + ' ' + entry.surName).toLowerCase()
  const club = (entry.clubName || '').toLowerCase()
  return name.includes(term) || club.includes(term)
}

const filteredConfirmedEntries = computed<IndividualEntry[]>(() => {
  return confirmedEntries.value.filter(matchesSearch)
})

const filteredWaitlistEntries = computed<IndividualEntry[]>(() => {
  return waitlistEntries.value.filter(matchesSearch)
})

const hasWaitlist = computed(() => {
  return waitlistEntries.value.length > 0
})

const isWaitListClosed = computed(() => {
  const isWaitClosed = isWaitingListCloseed(props.storeCompResultsState.competitionOnTheDay.options)
  return isWaitClosed
})
</script>

<style scoped>
.waitlist-badge {
  display: inline-block;
  padding: 2px 8px;
  background-color: var(--amber-100, #fef3c7);
  color: var(--amber-800, #92400e);
  border-radius: 4px;
  font-size: 0.75rem;
  font-weight: 600;
  line-height: 1;
  white-space: nowrap;
}

.waitlist-count-badge {
  display: inline-block;
  padding: 2px 8px;
  background-color: var(--amber-100, #fef3c7);
  color: var(--amber-800, #92400e);
  border-radius: 4px;
  font-size: 0.75rem;
  font-weight: 600;
  line-height: 1;
  white-space: nowrap;
}

.e4s-waitlist-banner {
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  gap: 12px;
  padding: 12px 16px;
  background-color: var(--amber-50, #fffbeb);
  border: 1px solid var(--amber-300, #fcd34d);
  border-radius: 8px;
}

.e4s-waitlist-banner-icon {
  flex-shrink: 0;
  width: 24px;
  height: 24px;
  background-color: var(--amber-600, #d97706);
  border-radius: 50%;
  position: relative;
  margin-top: 2px;
}

.e4s-waitlist-banner-icon::before {
  content: '!';
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  color: white;
  font-weight: 700;
  font-size: 16px;
  line-height: 1;
}

.e4s-waitlist-banner-text {
  flex: 1;
}

.e4s-waitlist-banner-title {
  font-weight: 700;
  font-size: 0.875rem;
  color: var(--amber-900, #78350f);
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 4px;
}

.e4s-waitlist-banner-subtitle {
  font-size: 0.8125rem;
  color: var(--amber-800, #92400e);
  line-height: 1.4;
}

.e4s-clock-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  flex-shrink: 0;
}

.e4s-clock-icon svg {
  width: 100%;
  height: 100%;
  color: var(--amber-600, #d97706);
}

.waitlist-row {
  background-color: var(--amber-50, #fffbeb);
}

.waitlist-row:hover {
  background-color: var(--amber-100, #fef3c7);
}

.e4s-waitlist-closed-banner {
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  gap: 12px;
  padding: 12px 16px;
  background-color: var(--red-50, #fef2f2);
  border: 2px solid var(--red-600, #dc2626);
  border-radius: 8px;
  margin-top: 8px;
}

.e4s-waitlist-closed-icon {
  flex-shrink: 0;
  width: 28px;
  height: 28px;
  background-color: var(--red-600, #dc2626);
  border-radius: 50%;
  position: relative;
  margin-top: 0;
}

.e4s-waitlist-closed-icon::before {
  content: '\2716';
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  color: white;
  font-weight: 700;
  font-size: 16px;
  line-height: 1;
}

.e4s-waitlist-closed-text {
  flex: 1;
}

.e4s-waitlist-closed-title {
  font-weight: 800;
  font-size: 1rem;
  color: var(--red-700, #b91c1c);
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 4px;
}

.e4s-waitlist-closed-subtitle {
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--red-700, #b91c1c);
  line-height: 1.4;
}
</style>
